import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  CreditCard, 
  Shield, 
  ArrowLeft, 
  Check,
  Lock,
  Calendar,
  DollarSign,
  Star,
  Crown,
  TrendingUp,
  Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import Logo from '@/components/Logo';

const PaymentSetup = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(false);
  
  const selectedPlan = searchParams.get('plan') || 'delicious';
  const authProvider = searchParams.get('provider') || 'google';

  // Mock payment form data
  const [formData, setFormData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    nameOnCard: '',
    billingEmail: ''
  });

  const planDetails = {
    yummy: {
      name: 'Yummy',
      price: 29.99,
      icon: Zap,
      gradient: 'from-green-400 to-green-600',
      features: ['AI Menu Assistant', 'Basic Menu Management', 'Email Support']
    },
    delicious: {
      name: 'Delicious',
      price: 49.99,
      icon: TrendingUp,
      gradient: 'from-blue-400 to-blue-600',
      features: ['Everything in Yummy', 'Mobile Analytics', 'Customer Insights', 'AI Images (5/month)']
    },
    gourmet: {
      name: 'Gourmet',
      price: 99.99,
      icon: Crown,
      gradient: 'from-purple-400 to-purple-600',
      features: ['Everything in Delicious', 'Advanced AI Insights', 'Automated Promotions', 'AI Images (50/month)']
    }
  };

  const currentPlan = planDetails[selectedPlan as keyof typeof planDetails];

  useEffect(() => {
    // If no plan selected, redirect back to plan selection
    if (!currentPlan) {
      navigate('/plan-selection');
    }
  }, [currentPlan, navigate]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePayment = async () => {
    setIsProcessing(true);

    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Store payment method for onboarding
      localStorage.setItem('payment_method', 'stripe_card');
      localStorage.setItem('selected_plan', selectedPlan);
      localStorage.setItem('auth_provider', authProvider);

      toast({
        title: "Payment method saved!",
        description: `Your ${currentPlan.name} plan is ready. Complete your restaurant setup to activate.`,
      });

      // Navigate to onboarding
      navigate('/restaurant-setup');

    } catch (error) {
      toast({
        title: "Payment failed",
        description: "Please check your payment details and try again.",
        variant: "destructive"
      });
      setIsProcessing(false);
    }
  };

  const handleBackToPlanSelection = () => {
    navigate('/plan-selection');
  };

  if (!currentPlan) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5]">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mb-6">
            <Logo className="text-white h-8" />
          </div>
          
          <button
            onClick={handleBackToPlanSelection}
            className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to plans
          </button>

          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Complete Your Payment Setup
          </h1>
          
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            Secure your {currentPlan.name} plan and start your 30-day free trial immediately.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Plan Summary */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6"
          >
            <h2 className="text-2xl font-bold text-white mb-6">Order Summary</h2>
            
            {/* Selected Plan */}
            <div className="bg-white/10 rounded-xl p-6 mb-6">
              <div className="flex items-center gap-4 mb-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${currentPlan.gradient} flex items-center justify-center`}>
                  <currentPlan.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{currentPlan.name} Plan</h3>
                  <p className="text-white/60">Monthly subscription</p>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                {currentPlan.features.slice(0, 3).map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-400" />
                    <span className="text-white/80 text-sm">{feature}</span>
                  </div>
                ))}
                {currentPlan.features.length > 3 && (
                  <p className="text-white/60 text-sm">+ {currentPlan.features.length - 3} more features</p>
                )}
              </div>

              <div className="border-t border-white/20 pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-white/80">Monthly cost:</span>
                  <span className="text-2xl font-bold text-white">${currentPlan.price}</span>
                </div>
              </div>
            </div>

            {/* Trial Benefits */}
            <div className="bg-gradient-to-r from-yellow-400/20 to-orange-400/20 rounded-xl border border-yellow-400/30 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-5 h-5 text-yellow-400" />
                <span className="font-semibold text-white">30-Day FREE Trial</span>
              </div>
              <p className="text-white/80 text-sm mb-3">
                Start immediately with full access. No charges until your trial ends.
              </p>
              
              <div className="space-y-1 text-xs text-white/60">
                <div className="flex items-center gap-2">
                  <Calendar className="w-3 h-3" />
                  <span>Trial ends: {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <DollarSign className="w-3 h-3" />
                  <span>First charge: ${currentPlan.price} on trial end</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Payment Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6"
          >
            <div className="flex items-center gap-2 mb-6">
              <Shield className="w-6 h-6 text-green-400" />
              <h2 className="text-2xl font-bold text-white">Secure Payment</h2>
              <Lock className="w-4 h-4 text-white/60" />
            </div>

            <div className="space-y-4">
              {/* Card Number */}
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">
                  Card Number
                </label>
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="1234 5678 9012 3456"
                    value={formData.cardNumber}
                    onChange={(e) => handleInputChange('cardNumber', e.target.value)}
                    className="bg-white/10 border-white/30 text-white placeholder:text-white/50 pl-12"
                  />
                  <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/60" />
                </div>
              </div>

              {/* Expiry and CVV */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Expiry Date
                  </label>
                  <Input
                    type="text"
                    placeholder="MM/YY"
                    value={formData.expiryDate}
                    onChange={(e) => handleInputChange('expiryDate', e.target.value)}
                    className="bg-white/10 border-white/30 text-white placeholder:text-white/50"
                  />
                </div>
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    CVV
                  </label>
                  <Input
                    type="text"
                    placeholder="123"
                    value={formData.cvv}
                    onChange={(e) => handleInputChange('cvv', e.target.value)}
                    className="bg-white/10 border-white/30 text-white placeholder:text-white/50"
                  />
                </div>
              </div>

              {/* Name on Card */}
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">
                  Name on Card
                </label>
                <Input
                  type="text"
                  placeholder="John Smith"
                  value={formData.nameOnCard}
                  onChange={(e) => handleInputChange('nameOnCard', e.target.value)}
                  className="bg-white/10 border-white/30 text-white placeholder:text-white/50"
                />
              </div>

              {/* Billing Email */}
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">
                  Billing Email
                </label>
                <Input
                  type="email"
                  placeholder="john@restaurant.com"
                  value={formData.billingEmail}
                  onChange={(e) => handleInputChange('billingEmail', e.target.value)}
                  className="bg-white/10 border-white/30 text-white placeholder:text-white/50"
                />
              </div>
            </div>

            {/* Security Notice */}
            <div className="bg-white/5 rounded-lg p-4 mt-6">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-green-400" />
                <span className="text-white/80 text-sm font-medium">Secure & Encrypted</span>
              </div>
              <p className="text-white/60 text-xs">
                Your payment information is encrypted and secure. We never store your card details.
              </p>
            </div>

            {/* Action Button */}
            <Button
              onClick={handlePayment}
              disabled={isProcessing}
              className="w-full py-6 mt-6 bg-gradient-to-r from-green-400 to-green-600 hover:from-green-300 hover:to-green-500 text-white text-lg font-semibold rounded-xl transition-all duration-300"
            >
              {isProcessing ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Processing Payment...
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <Lock className="w-5 h-5" />
                  Start FREE Trial Now
                </div>
              )}
            </Button>

            <p className="text-white/60 text-xs text-center mt-4">
              * You won't be charged until your 30-day free trial ends. Cancel anytime.
            </p>
          </motion.div>
        </div>

        {/* Trust Indicators */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-8"
        >
          <div className="flex justify-center items-center gap-8 text-white/40">
            <div className="flex items-center gap-1">
              <Shield className="w-4 h-4" />
              <span className="text-xs">256-bit SSL</span>
            </div>
            <div className="flex items-center gap-1">
              <Lock className="w-4 h-4" />
              <span className="text-xs">PCI Compliant</span>
            </div>
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4" />
              <span className="text-xs">Money-back Guarantee</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PaymentSetup;